import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as teamsApi from '~/services/api/teams';

import type { CreateTeamPayload, Team, TeamListItem, UpdateTeamRequest } from '~/types';

/**
 * Query key factory for teams.
 */
export const teamKeys = {
  all: ['teams'] as const,
  detail: (teamId: string) => [...teamKeys.details(), teamId] as const,
  details: () => [...teamKeys.all, 'detail'] as const,
  list: (orgId: string) => [...teamKeys.lists(), orgId] as const,
  lists: () => [...teamKeys.all, 'list'] as const,
};

/**
 * Create a new team.
 */
export function useCreateTeam(): ReturnType<
  typeof useMutation<
    Awaited<ReturnType<typeof teamsApi.createTeam>>,
    Error,
    { data: CreateTeamPayload; orgId: string }
  >
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, orgId }) => teamsApi.createTeam(orgId, data),
    onSuccess: (_newTeam, { orgId }) => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.list(orgId) });
    },
  });
}

/**
 * Delete a team.
 */
export function useDeleteTeam(): ReturnType<
  typeof useMutation<void, Error, { orgId: string; teamId: string }>
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ teamId }) => teamsApi.deleteTeam(teamId),
    onSuccess: (_data, { orgId, teamId }) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: teamKeys.detail(teamId) });
      // Invalidate team list
      void queryClient.invalidateQueries({ queryKey: teamKeys.list(orgId) });
    },
  });
}

/**
 * Fetch teams for an organization.
 */
export function useOrganizationTeams(orgId: string | undefined): {
  error: unknown;
  isLoading: boolean;
  teams: TeamListItem[];
} {
  const {
    data: teams = [],
    error,
    isLoading,
  } = useQuery({
    queryKey: teamKeys.list(orgId ?? ''),
    queryFn: () => (orgId ? teamsApi.getOrganizationTeams(orgId) : []),
    enabled: !!orgId,
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
  });

  return { error, isLoading, teams };
}

/**
 * Fetch team details.
 */
export function useTeamDetail(teamId: string | undefined): {
  error: unknown;
  isLoading: boolean;
  team: Team | null;
} {
  const {
    data: team,
    error,
    isLoading,
  } = useQuery({
    queryKey: teamKeys.detail(teamId ?? ''),
    queryFn: () => (teamId ? teamsApi.getTeamDetail(teamId) : null),
    enabled: !!teamId,
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
  });

  return { error, isLoading, team: team ?? null };
}

/**
 * Update a team.
 */
export function useUpdateTeam(): ReturnType<
  typeof useMutation<
    Awaited<ReturnType<typeof teamsApi.updateTeam>>,
    Error,
    { data: UpdateTeamRequest; orgId?: string; teamId: string }
  >
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, teamId }) => teamsApi.updateTeam(teamId, data),
    onSuccess: (_updatedTeam, { orgId, teamId }) => {
      // Invalidate team detail
      void queryClient.invalidateQueries({ queryKey: teamKeys.detail(teamId) });
      // Invalidate team list if orgId provided
      if (orgId) {
        void queryClient.invalidateQueries({ queryKey: teamKeys.list(orgId) });
      }
    },
  });
}
